import React, { useEffect, useState } from 'react'
import SearchInput from '../../../../../components/ui/inputs/SearchInput'
import SearchSelect from '../../../../../components/ui/selects/SearchSelect'
import Pagination from '../../../../../components/ui/pagination/Pagination'
import { camelizeKeys } from '../../../../../utils/camelizeKeys'
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useToast } from '../../../../../components/ui/toast/ToastContext'
import MovementRow from './movements/MovementRow'

const initialPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0
}

const MovementsTab = ({ clientId, scope }) => {
  const { addToast } = useToast()
  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState('all')
  const [movements, setMovements] = useState([])
  const [updatedAt, setUpdatedAt] = useState('-')
  const [pagination, setPagination] = useState(initialPagination)
  const [launchTypes, setLaunchTypes] = useState([])

  const [filters, setFilters] = useState({
    search: '',
    launchType: ''
  })

  const [sortBy, setSortBy] = useState('movement_date')
  const [sortOrder, setSortOrder] = useState('desc')

  useEffect(() => {
    fetchAccounts()
    fetchLaunchTypes()
  }, [clientId, scope])

  useEffect(() => {
    if (selectedAccount !== null) fetchMovements(1)
  }, [selectedAccount, filters, sortBy, sortOrder])

  const fetchAccounts = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/movements/accounts?scope=${scope}`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setAccounts(camelData.accounts)
        setSelectedAccount('all')
      })
      .catch(() => addToast({ type: 'error', message: 'Erro ao carregar contas BTG' }))
  }

  const fetchLaunchTypes = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/movements/launch_types?scope=${scope}`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setLaunchTypes(camelData.launchTypes)
      })
      .catch(() => addToast({ type: 'error', message: 'Erro ao carregar tipos de movimentação' }))
  }

  const buildQuery = (params) => {
    const snakeParams = snakecaseKeys(params)
    return Object.entries(snakeParams)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')
  }

  const fetchMovements = (page = 1) => {
    const query = buildQuery({
      ...filters,
      page,
      sort: sortBy,
      order: sortOrder,
      accountId: selectedAccount
    })

    fetch(`/app/planner/client/${clientId}/dashboard/movements?scope=${scope}&${query}`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setMovements(camelData.movements)
        setPagination(camelData.pagination)
        setUpdatedAt(camelData.updatedAt)
      })
      .catch(() => addToast({ type: 'error', message: 'Erro ao carregar movimentações' }))
  }

  const handleSortClick = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortBy(field)
    setSortOrder(newOrder)
  }

  const renderSortIcon = (field) => {
    if (sortBy !== field) return null
    return sortOrder === 'asc'
      ? <ChevronUp size={14} className="text-tealMedium ml-1" />
      : <ChevronDown size={14} className="text-tealMedium ml-1" />
  }

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="w-full flex items-center justify-between">
        <div className="flex gap-4">
          <div className="w-96">
            <SearchInput
              name="search"
              placeholder="Pesquisar ativo..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          
          <div className="w-64">
            <SearchSelect
              name="launchType"
              placeholder="Tipo da movimentação"
              value={filters.launchType}
              onChange={(e) => setFilters(prev => ({ ...prev, launchType: e.target.value }))}
              options={launchTypes.map(type => ({
                label: type.charAt(0).toUpperCase() + type.slice(1),
                value: type
              }))}
            />
          </div>
          <div className="w-64">
            <SearchSelect
              name="accountId"
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              placeholder="Selecionar conta BTG"
              allText="Todas as contas"
              options={accounts.map(acc => ({
                label: acc.displayName,
                value: acc.id
              }))}
            />
          </div>
        </div>

        <div className="flex">
          <span><span className="font-semibold">Atualizado em: </span>{updatedAt}</span>
        </div>
      </div>

      {/* Tabela */}
      <table className="w-full table-fixed text-left">
        <thead>
          <tr className="text-base text-blueDarker font-semibold">
            <th className="pl-2 py-2 w-1/5">
              <button onClick={() => handleSortClick('asset')} className="flex items-center hover:opacity-80 gap-1 text-blueDarker font-semibold text-base">
                Ativo
                {renderSortIcon('asset')}
              </button>
            </th>
            <th className="pl-2 py-2">
              <button onClick={() => handleSortClick('launch_type')} className="flex items-center hover:opacity-80 gap-1 text-blueDarker font-semibold text-base">
                Tipo
                {renderSortIcon('laaunch_type')}
              </button>
            </th>
            <th className="pl-2 py-2">Conta</th>
            <th className="pl-2 py-2">
              <button onClick={() => handleSortClick('movement_date')} className="flex items-center hover:opacity-80 gap-1 text-blueDarker font-semibold text-base">
                Data
                {renderSortIcon('movement_date')}
              </button>
            </th>
            <th className="pl-2 py-2">
              <button onClick={() => handleSortClick('gross_value')} className="flex items-center hover:opacity-80 gap-1 text-blueDarker font-semibold text-base">
                Valor Bruto
                {renderSortIcon('gross_value')}
              </button>
            </th>
            <th className="pl-2 pr-4 py-2 w-2/5">
              <button onClick={() => handleSortClick('movement_history')} className="flex items-center hover:opacity-80 gap-1 text-blueDarker font-semibold text-base">
                Descrição
                {renderSortIcon('movement_history')}
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="text-blueSteel text-sm">
          {movements.map((movement, index) => (
            <MovementRow
              key={movement.id || index}
              movement={movement}
              index={index}
            />
          ))}
        </tbody>
      </table>

      {/* Paginação */}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={(newPage) => fetchMovements(newPage)}
      />
    </div>
  )
}

export default MovementsTab
